/* eslint no-underscore-dangle: 0 */
/* eslint-disable no-await-in-loop */
/**
 * Basado en blockly-samples/realtime (@author navil@google.com).
 */
import WorkSpaceEvents from './workSpaceEvents';
import Position from './position';
import { userRoles } from '../../../../sections/collaboration/features/constants';

export default class WorkSpaceClient {
  constructor(socket, Blockly, workspace, workspaceId, projectId) {
    this.blockly = Blockly;
    this.workspace = workspace;
    this.projectId = projectId;
    this.workspaceId = workspaceId;
    this.collabEvents = new WorkSpaceEvents(socket, this.blockly, this.workspace, this.workspaceId, projectId);
    this.lastSync = 0;
    this.inProgress = [];
    this.notSent = [];
    this.activeChanges = [];
    this.writeInProgress = false;
    this.counter = 0;
    this.serverEvents = [];
    this.updateInProgress = false;
    this.usersList = [];
    this.socket = socket;
  }

  setUsersList(usersList) {
    this.usersList = usersList;
  }

  start() {
    return new Promise((resolve) => {
      // Referencias guardadas para removerlas en dispose(): el socket vive más que
      // esta instancia y los handlers apilados duplican los eventos.
      this.onAuthenticated = () => {
        this.getSnapshot().catch(() => this.blockly.Events.enable());
      };
      this.onManagementIsBack = (role) => {
        if (role === userRoles.LEADER) {
          setTimeout(() => {
            this.getSnapshot().catch(() => this.blockly.Events.enable());
          }, 2000);
        }
      };
      this.socket.on('authenticated', this.onAuthenticated);
      this.socket.on('managementIsBack', this.onManagementIsBack);

      this.getSnapshot().then(() => {
        resolve();
      }).catch((err) => {
        this.blockly.Events.enable();
        // eslint-disable-next-line no-console
        console.log('================START SESSION ERROR', err);
      });
    });
  }

  // Lo llama el cleanup del efecto en components/blockly.jsx; sin esto las
  // suscripciones quedan colgando entre re-montajes y se duplican bloques.
  dispose() {
    if (this.onAuthenticated) {
      this.socket.off('authenticated', this.onAuthenticated);
    }
    if (this.onManagementIsBack) {
      this.socket.off('managementIsBack', this.onManagementIsBack);
    }
    this.collabEvents.dispose();
  }

  getSnapshot() {
    return new Promise((resolve, reject) => {
      this.lastSync = 0;
      this.inProgress = [];
      this.notSent = [];
      this.activeChanges = [];
      this.writeInProgress = false;
      this.counter = 0;
      this.serverEvents = [];
      this.updateInProgress = false;

      this.blockly.Events.disable();
      this.collabEvents.getSnapshot().then((snapshot) => {
        this.loadSnapshot_(snapshot);
        this.blockly.Events.enable();
        this.collabEvents.getBroadcast(this.addServerEvents_.bind(this));
        resolve();
      }).catch((err) => reject(err));
    });
  }

  // Acepta XML además de JSON: un snapshot guardado por el editor legacy puede
  // llegar todavía en XML.
  loadSnapshot_(snapshot) {
    const saved = snapshot?.xml;
    if (!saved) return;
    this.workspace.clear();
    if (typeof saved === 'string' && saved.trim().startsWith('<')) {
      this.blockly.Xml.domToWorkspace(this.blockly.utils.xml.textToDom(saved), this.workspace);
    } else if (typeof saved === 'object' && !Array.isArray(saved)) {
      this.blockly.serialization.workspaces.load(saved, this.workspace);
    } else if (typeof saved === 'string' && saved.trim().startsWith('{')) {
      this.blockly.serialization.workspaces.load(JSON.parse(saved), this.workspace);
    }
  }

  addEvent(event) {
    this.activeChanges.push(event);
  }

  flushEvents() {
    this.notSent = this.notSent.concat(this.activeChanges);
    this.activeChanges = [];
    this.updateServer_();
  }

  async updateServer_() {
    if (this.writeInProgress || this.notSent.length === 0) {
      return;
    }
    this.writeInProgress = true;
    while (this.notSent.length > 0) {
      await this.writeToDatabase_();
    }
    this.writeInProgress = false;
  }

  async writeToDatabase_() {
    this.beginWrite_();
    try {
      await this.collabEvents.writeEvents(this.inProgress[this.inProgress.length - 1]);
      this.endWrite_(true);
    } catch {
      this.endWrite_(false);
      throw Error('Failed to write to database.');
    }
  }

  beginWrite_() {
    this.writeInProgress = true;
    this.inProgress.push({
      workspaceId: this.workspaceId,
      entryNumber: this.counter,
      events: this.blockly.Events.filter(this.notSent),
    });
    this.counter += 1;
    this.notSent = [];
  }

  endWrite_(success) {
    if (!success) {
      this.notSent = this.inProgress[0].events.concat(this.notSent);
      this.inProgress = [];
      this.counter -= 1;
    }
    this.writeInProgress = false;
  }

  async addServerEvents_(newServerEventsParam) {
    const newServerEvents = newServerEventsParam;
    if (newServerEvents.length === 0) {
      return;
    }
    this.lastSync = newServerEvents[newServerEvents.length - 1].serverId;
    this.serverEvents.push(...newServerEvents);
    this.updateWorkspace_();
  }

  async updateWorkspace_() {
    if (this.updateInProgress || this.serverEvents === 0) {
      return;
    }
    this.updateInProgress = true;
    while (this.serverEvents.length > 0) {
      const newServerEvents = this.serverEvents;
      this.serverEvents = [];
      const eventQueue = await this.processQueryResults_(newServerEvents);
      this.updateInProgress = false;

      eventQueue.forEach((event) => {
        this.blockly.Events.disable();
        try {
          // No se reaplica la creación del bloque inicial del canvas (initial_block).
          const isInitialBlockCreate = event.event.json
            && JSON.stringify(event.event.json).includes('initial_block');
          if (!isInitialBlockCreate) {
            event.event.run(event.forward);
          }
        } catch (err) {
          // enable() siempre (en el finally): si run() tira, `Events.disable()` queda desbalanceado
          // y se rompen undo, el broadcast local y los markers.
          // eslint-disable-next-line no-console
          console.warn('[RB-129] evento de colaboración no aplicado:', err?.message);
        } finally {
          this.blockly.Events.enable();
        }
        this.updateMarkerPositions_(event.event.position);
      });
      this.workspace.fireChangeListener?.('resize');
    }
  }

  // markBlock no es de Blockly: lo instala userMarker.js al importarse; de ahí el guard.
  updateMarkerPositions_(positionUpdate) {
    if (!positionUpdate || !positionUpdate.position || !positionUpdate.id || positionUpdate.id === this.workspaceId) {
      return;
    }
    if (typeof this.workspace.markBlock !== 'function') {
      return;
    }
    const color = this.usersList.filter((u) => u.User.id === positionUpdate.id)[0]?.color || '#FFF200';
    this.workspace.markBlock(positionUpdate.id, positionUpdate.position.blockId, true, color);
  }

  handleUnmarkPosition(event) {
    const position = Position.fromEvent(event, false);
    if (position && typeof this.workspace.markBlock === 'function') {
      this.workspace.markBlock(this.workspaceId, position.blockId, false);
    }
  }

  // Converge con el server: deshace los cambios locales sin confirmar, aplica
  // los del server y reaplica los locales encima.
  processQueryResults_(entries) {
    const eventQueue = [];

    if (entries.length === 0) {
      return eventQueue;
    }

    this.lastSync = entries[entries.length - 1].serverId;

    if (this.notSent.length === 0 && this.inProgress.length === 0) {
      entries.forEach((entry) => {
        eventQueue.push(...this.createWorkspaceActions_(entry.events, true));
      });
      return eventQueue;
    }

    if (this.inProgress.length > 0
      && entries[0].workspaceId === this.workspaceId
      && entries[0].entryNumber === this.inProgress[0].entryNumber) {
      entries.shift();
      this.inProgress = [];
    }

    if (entries.length > 0) {
      eventQueue.push(...this.createWorkspaceActions_(this.notSent.slice().reverse(), false));

      if (this.inProgress.length > 0) {
        this.inProgress.slice().reverse().forEach((entry) => {
          eventQueue.push(...this.createWorkspaceActions_(entry.events.slice().reverse(), false));
        });
      }
      entries.forEach((entry) => {
        if (this.inProgress.length > 0
          && entry.workspaceId === this.inProgress[0].workspaceId
          && entry.entryNumber === this.inProgress[0].entryNumber) {
          eventQueue.push(...this.createWorkspaceActions_(this.inProgress[0].events, true));
          this.inProgress.shift();
        } else {
          eventQueue.push(...this.createWorkspaceActions_(entry.events, true));
        }
      });
      if (this.inProgress.length > 0) {
        eventQueue.push(...this.createWorkspaceActions_(this.inProgress[0].events, true));
      }
      eventQueue.push(...this.createWorkspaceActions_(this.notSent, true));
    }
    return eventQueue;
  }

  createWorkspaceActions_(events, forward) {
    return events.map((event) => ({ event, forward }));
  }
}
